import React, { useEffect, useState } from "react";
import { ActionIcon } from "@mantine/core";
import { IconTrash } from "@tabler/icons-react";
import { getAuthHeaders } from "../../api/authHelper";
import {
    Badge,
    Button,
    Center,
    Group,
    Loader,
    Modal,
    Paper,
    Stack,
    Text,
} from "@mantine/core";

export type ShiftParticipant = {
    personId: number;
    firstName: string;
    lastName: string;
    email?: string;
    phone?: string;
    status: string;
    createdAt?: string;
    checkInTime?: string | null;
};

export type ShiftParticipantsResponse = {
    shiftId: number;
    maxParticipants: number;
    counts: { confirmed: number; waitlist: number; cancelled: number };
    confirmed: ShiftParticipant[];
    waitlist: ShiftParticipant[];
    cancelled: ShiftParticipant[];
};

export default function ParticipantsModal({
    opened,
    onClose,
    title,
    loading: externalLoading,
    data: externalData,
    onRefresh,
}: {
    opened: boolean;
    onClose: () => void;
    title: string;
    loading: boolean;
    data: ShiftParticipantsResponse | null;
    onRefresh?: () => Promise<void> | void;
}) {
    const apiUrl = process.env.REACT_APP_API_URL as string;
    const [movingPersonId, setMovingPersonId] = useState<number | null>(null);

    const [data, setData] = useState<ShiftParticipantsResponse | null>(null);
    const [loading, setLoading] = useState(false);

    const effectiveData = externalData ?? data;
    const effectiveLoading = externalData ? externalLoading : loading;

    const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
    const isFull =
        !!effectiveData &&
        effectiveData.maxParticipants > 0 &&
        effectiveData.counts.confirmed >= effectiveData.maxParticipants;

    const isCheckedIn = (p: ShiftParticipant) => !!p.checkInTime;

    useEffect(() => {
        if (!opened || externalData) return;

        const shiftIdMatch = title?.match(/\d+/);
        if (!shiftIdMatch) return;

        const shiftId = shiftIdMatch[0];

        const load = async () => {
            try {
                setLoading(true);

                const res = await fetch(
                    `${apiUrl}/api/shifts/${shiftId}/participants`
                );

                const json = await res.json();
                setData(json);
            } catch (err) {
                console.error(err);
                setData(null);
            } finally {
                setLoading(false);
            }
        };

        load();
    }, [opened, externalData, apiUrl, title]);

    const handleMoveToConfirmed = async (personId: number) => {
        if (!effectiveData?.shiftId) return;

        try {
            setMovingPersonId(personId);

            const res = await fetch(
                `${apiUrl}/api/shifts/${effectiveData.shiftId}/register`,
                {
                    method: "POST",
                    headers: {
                        ...getAuthHeaders(),
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        personId,
                        status: "Tilmeldt",
                    }),
                }
            );

            if (!res.ok) {
                const errorText = await res.text();
                throw new Error(
                    `Move failed (${res.status}): ${errorText}`
                );
            }

            if (onRefresh) await onRefresh();
        } catch (err) {
            console.error("Move to confirmed failed:", err);
            alert("Kunne ikke flytte personen til tilmeldt.");
        } finally {
            setMovingPersonId(null);
        }
    };

    const formatTime = (iso?: string | null) => {
        if (!iso) return null;

        return new Date(iso).toLocaleString("da-DK", {
            dateStyle: "short",
            timeStyle: "short",
        });
    };

    const handleRemoveFromShift = async (personId: number) => {
        if (!effectiveData?.shiftId) return;

        try {
            setMovingPersonId(personId);

            const res = await fetch(
                `${apiUrl}/api/shifts/${effectiveData.shiftId}/unregister/${personId}`,
                {
                    method: "DELETE",
                    headers: getAuthHeaders(),
                }
            );

            if (!res.ok) {
                const errorText = await res.text();
                throw new Error(errorText || "Remove failed");
            }

            if (onRefresh) await onRefresh();
        } catch (err) {
            console.error(err);
            alert("Kunne ikke fjerne personen fra shiftet.");
        } finally {
            setMovingPersonId(null);
        }
    };



    const handlePrintParticipants = () => {
        if (!effectiveData) return;

        // Only include confirmed participants (Tilmeldt)
        const participants = effectiveData.confirmed
            .filter((p) => p.status === "Tilmeldt")
            .sort((a, b) =>
                `${a.firstName} ${a.lastName}`.localeCompare(
                    `${b.firstName} ${b.lastName}`,
                    "da"
                )
            );

        const escapeHtml = (value: string) =>
            value.replace(/[&<>"']/g, (char) => {
                const entities: Record<string, string> = {
                    "&": "&amp;",
                    "<": "&lt;",
                    ">": "&gt;",
                    '"': "&quot;",
                    "'": "&#39;",
                };
                return entities[char];
            });

        const rows = participants
            .map(
                (p, index) => `
                <tr>
                    <td>${index + 1}</td>
                    <td>${escapeHtml(`${p.firstName} ${p.lastName}`)}</td>
                </tr>
            `
            )
            .join("");

        const printWindow = window.open("", "_blank");

        if (!printWindow) {
            alert("Tillad pop-ups for at udskrive deltagerlisten.");
            return;
        }

        printWindow.document.open();
        printWindow.document.write(`
        <!DOCTYPE html>
        <html lang="da">
        <head>
            <meta charset="UTF-8" />
            <title>Deltagerliste - ${escapeHtml(title)}</title>
            <style>
                @page {
                    size: A4 portrait;
                    margin: 15mm;
                }

                body {
                    font-family: Arial, sans-serif;
                    color: #222;
                    margin: 0;
                }

                h1 {
                    font-size: 26px;
                    margin-bottom: 8px;
                }

                .subtitle {
                    font-size: 15px;
                    color: #555;
                    margin-bottom: 20px;
                }

                .summary {
                    font-size: 14px;
                    margin-bottom: 20px;
                }

                table {
                    width: 100%;
                    border-collapse: collapse;
                    font-size: 15px;
                }

                th {
                    background: #eee;
                    text-align: left;
                    padding: 12px;
                    border: 1px solid #ccc;
                }

                td {
                    padding: 12px;
                    border: 1px solid #ccc;
                }

                th:first-child,
                td:first-child {
                    width: 50px;
                }

                tr {
                    break-inside: avoid;
                    page-break-inside: avoid;
                }

                thead {
                    display: table-header-group;
                }

                .footer {
                    margin-top: 25px;
                    font-size: 12px;
                    color: #777;
                }
            </style>
        </head>
        <body>
            <h1>Deltagerliste</h1>

            <div class="subtitle">
                ${escapeHtml(title)}
            </div>

            <div class="summary">
                <strong>Antal tilmeldte:</strong>
                ${participants.length}
            </div>

            <table>
                <thead>
                    <tr>
                        <th>Nr.</th>
                        <th>Navn</th>
                    </tr>
                </thead>
                <tbody>
                    ${rows}
                </tbody>
            </table>

            <div class="footer">
                Udskrevet: ${new Date().toLocaleString("da-DK")}
            </div>
        </body>
        </html>
    `);

        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
    };



    return (
        <Modal opened={opened} onClose={onClose} title={title} size="lg">
            {effectiveLoading ? (
                <Center>
                    <Loader />
                </Center>
            ) : !effectiveData ? (
                <Text c="dimmed">Ingen data.</Text>
            ) : (

                        <Stack gap="md">
                            <Group justify="space-between">
                                <Group gap="xs">
                                    <Badge variant="light">
                                        Tilmeldt: {effectiveData.counts.confirmed}
                                    </Badge>
                                    <Badge variant="light">
                                        Venteliste: {effectiveData.counts.waitlist}
                                    </Badge>
                                    <Badge variant="light">
                                        Frameldt: {effectiveData.counts.cancelled}
                                    </Badge>
                                    <Badge color="gray" variant="light">
                                        Max: {effectiveData.maxParticipants}
                                    </Badge>
                                </Group>

                                <Button
                                    color="blue"
                                    variant="filled"
                                    onClick={handlePrintParticipants}
                                    disabled={effectiveData.confirmed.length === 0}
                                >
                                    Print tilmeldte
                                </Button>
                            </Group>

                    <Stack gap="xs">
                        <Text fw={700}>Tilmeldte</Text>

                        {effectiveData.confirmed.map((p) => {

                            return (
                                <Paper key={`${p.personId}-c`} withBorder p="sm" radius="md">
                                    <Group justify="space-between">
                                        <div>
                                            <Text fw={600}>
                                                {p.firstName} {p.lastName}
                                            </Text>
                                            <Text size="sm" c="dimmed">
                                                {p.email || ""}
                                                {p.email && p.phone ? " · " : ""}
                                                {p.phone || ""}
                                            </Text>
                                        </div>

                                        <Group gap="xs">
                                            <Badge
                                                size="md"
                                                color={isCheckedIn(p) ? "green" : "red"}
                                                title={isCheckedIn(p) ? "Checked in" : "Not checked in"}
                                            />

                                            {p.checkInTime && (
                                                <Text size="xs" c="dimmed">
                                                    In: {formatTime(p.checkInTime)}
                                                </Text>
                                            )}
                                        </Group>
                                        <Badge color="green" variant="light">Tilmeldt</Badge>
                                        <ActionIcon
                                            color="red"
                                            variant="light"
                                            onClick={() => setConfirmDeleteId(p.personId)}
                                            disabled={movingPersonId === p.personId}
                                        >
                                            <IconTrash size={16} />
                                        </ActionIcon>
                                    </Group>
                                </Paper>
                            );
                        })}
                    </Stack>

                    <Stack gap="xs">
                        <Text fw={700}>Venteliste</Text>

                        {effectiveData.waitlist.map((p) => (
                            <Paper key={`${p.personId}-w`} withBorder p="sm" radius="md">
                                <Group justify="space-between" align="center">
                                    <div>
                                        <Text fw={600}>
                                            {p.firstName} {p.lastName}
                                        </Text>
                                    </div>

                                    <Group gap="sm">
                                        <Badge color="yellow" variant="light">
                                            Venteliste
                                        </Badge>

                                        <Button
                                            size="xs"
                                            disabled={isFull}
                                            onClick={() =>
                                                handleMoveToConfirmed(p.personId)
                                            }
                                            loading={movingPersonId === p.personId}
                                        >
                                            Flyt til tilmeldt
                                        </Button>
                                    </Group>
                                </Group>
                            </Paper>
                        ))}
                    </Stack>
                </Stack>
            )}
            <Modal
                opened={confirmDeleteId !== null}
                onClose={() => setConfirmDeleteId(null)}
                title="Bekræft sletning"
                centered
            >
                <Text size="sm">
                    Are you sure you want to remove this person from the shift?
                </Text>

                <Group justify="flex-end" mt="md">
                    <Button variant="default" onClick={() => setConfirmDeleteId(null)}>
                        Cancel
                    </Button>

                    <Button
                        color="red"
                        onClick={() => {
                            if (confirmDeleteId !== null) {
                                handleRemoveFromShift(confirmDeleteId);
                                setConfirmDeleteId(null);
                            }
                        }}
                    >
                        Delete
                    </Button>
                </Group>
            </Modal>
        </Modal>
    );
}